import { Canceler } from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RouteComponentProps, useHistory, useRouteMatch } from 'react-router-dom';
import { FixedTab, RouteTabs, VariableTab } from '../../../components/RouteTabs';
import useSnackbar from '../../../hooks/useSnackbar';
import api from '../../../services/api/base';
import { IGiftbox } from '../../../services/types';
import { RouteNames } from '../../config';
import CreateGiftBox from './CreateGiftBox';
import EditGiftBox from './EditGiftbox';
import GiftboxList from './GiftboxList';
import { getLiveGiftbox, getScheduledGiftbox, getUnscheduledGiftbox } from '../../../services/api/Giftbox';

interface IProps {}

interface IParamProps {
  giftboxId?: string;
}

const GiftBox = (props: IProps & RouteComponentProps): JSX.Element => {
  const path = RouteNames.Giftbox;
  const match = useRouteMatch<IParamProps>(`${path}/v/:giftboxId`);

  const history = useHistory();
  const giftboxId = Number(match?.params?.giftboxId);

  const initialGiftboxIds = giftboxId ? [giftboxId] : [];
  const [editingGiftboxIds, setEditingGiftboxIds] = useState<number[]>(initialGiftboxIds);

  const [liveGiftboxes, setLiveGiftboxes] = useState<IGiftbox[]>([]);
  const [scheduledGiftboxes, setScheduledGiftboxes] = useState<IGiftbox[]>([]);
  const [unscheduledGiftboxes, setUnscheduledGiftboxes] = useState<IGiftbox[]>([]);

  const liveGiftboxCanceler = useRef<Canceler>(() => undefined);
  const scheduledGiftboxCanceler = useRef<Canceler>(() => undefined);
  const unscheduledGiftboxCanceler = useRef<Canceler>(() => undefined);

  const snackbar = useSnackbar();

  const fetchData = useCallback(() => {
    fetchLiveGiftboxData();
    fetchScheduledGiftboxData();
    fetchUnscheduledGiftboxData();
  }, []);

  useEffect(() => {
    fetchData();

    return () => {
      liveGiftboxCanceler.current();
      scheduledGiftboxCanceler.current();
      unscheduledGiftboxCanceler.current();
    };
  }, [fetchData]);

  const fetchLiveGiftboxData = async () => {
    liveGiftboxCanceler.current();

    try {
      const [fetchPromise, cancel] = getLiveGiftbox();
      liveGiftboxCanceler.current = cancel;
      const result = await fetchPromise;

      if (result) {
        setLiveGiftboxes([result.data as IGiftbox]);
      } else {
        setLiveGiftboxes([]);
      }
    } catch (e) {
      if (api.isCancel(e)) return;
    }
  };

  const fetchScheduledGiftboxData = async () => {
    scheduledGiftboxCanceler.current();

    try {
      const [fetchPromise, cancel] = getScheduledGiftbox();
      scheduledGiftboxCanceler.current = cancel;
      const result = await fetchPromise;

      if (result) {
        setScheduledGiftboxes(result.data as IGiftbox[]);
      } else {
        setScheduledGiftboxes([]);
      }
    } catch (e) {
      if (api.isCancel(e)) return;
    }
  };

  const fetchUnscheduledGiftboxData = async () => {
    unscheduledGiftboxCanceler.current();

    try {
      const [fetchPromise, cancel] = getUnscheduledGiftbox();
      unscheduledGiftboxCanceler.current = cancel;
      const result = await fetchPromise;

      if (result) {
        setUnscheduledGiftboxes(result.data as IGiftbox[]);
      } else {
        setUnscheduledGiftboxes([]);
      }
    } catch (e) {
      if (api.isCancel(e)) return;
    }
  };

  const handleReload = useCallback(() => fetchData(), [fetchData]);

  const handleSelect = useCallback(
    (giftbox: IGiftbox) => {
      if (!giftbox || !giftbox.id) return;

      if (!editingGiftboxIds.find(id => id === giftbox.id)) {
        setEditingGiftboxIds([...editingGiftboxIds, giftbox.id]);
      }
    },
    [editingGiftboxIds],
  );

  const handleTabClose = useCallback(
    giftboxId => {
      setEditingGiftboxIds(editingGiftboxIds.filter(id => id !== giftboxId));
    },
    [editingGiftboxIds],
  );

  const getTabLabel = useCallback(
    giftboxId => {
      const currentEditingGiftbox = [...liveGiftboxes, ...scheduledGiftboxes, ...unscheduledGiftboxes].find(g => g.id == giftboxId);
      return currentEditingGiftbox ? currentEditingGiftbox.title : `VIEW ${giftboxId}`;
    },
    [liveGiftboxes, scheduledGiftboxes, unscheduledGiftboxes],
  );

  const handleCreate = useCallback(() => {
    fetchData();
    snackbar.success('Saved changes successfully.');

    history.push(RouteNames.Giftbox);
  }, [fetchData, snackbar, history]);

  const handleEdit = useCallback(() => {
    fetchData();
    snackbar.success('Saved changes successfully.');
  }, [fetchData, snackbar]);

  const getTabUri = useCallback(giftboxId => `v/${giftboxId}`, []);

  const keyExtractor = useCallback(giftboxId => `tab-floating-${giftboxId}`, []);

  const variableTabRenderer = useCallback(giftboxId => <EditGiftBox giftboxId={giftboxId} onEdit={handleEdit} />, [handleEdit]);

  return (
    <RouteTabs path={path}>
      <FixedTab label="Gift Box List">
        <GiftboxList
          liveGiftboxes={liveGiftboxes}
          scheduledGiftboxes={scheduledGiftboxes}
          unscheduledGiftboxes={unscheduledGiftboxes}
          onReload={handleReload}
          onSelect={handleSelect}
        />
      </FixedTab>
      <FixedTab label="Make a Gift Box" uri="create">
        <CreateGiftBox onCreate={handleCreate} />
      </FixedTab>
      <VariableTab<number>
        data={editingGiftboxIds}
        label={getTabLabel}
        uri={getTabUri}
        onClose={handleTabClose}
        keyExtractor={keyExtractor}
        render={variableTabRenderer}
      />
    </RouteTabs>
  );
};

export default GiftBox;
